import { useEffect, useRef } from 'react';

import Confetti from 'react-confetti';
import { useDispatch, useSelector } from 'react-redux';
import { useWindowSize } from 'react-use';

import { useNewVersion } from '@/hooks/useNewVersion';
import { useBillingBanner } from '@/modules/billing/hooks/useBillingBanner';
import { disableConfetti } from '@/redux/confetti/actions';
import { isConfettiActive } from '@/redux/confetti/selectors';

interface LayoutProps {
  children: React.ReactNode;
}

// Isolated so `useWindowSize` only subscribes to `resize` while confetti is active
// (otherwise every page re-renders on every resize tick).
const ConfettiOverlay = () => {
  const { width, height } = useWindowSize();
  return <Confetti width={width} height={height} recycle={false} initialVelocityY={6} />;
};

const Layout = ({ children }: LayoutProps) => {
  const dispatch = useDispatch();
  useNewVersion();
  useBillingBanner();

  const isConfettiEnabled = useSelector(isConfettiActive);

  const confettiTimeout = useRef<NodeJS.Timeout>(null);
  useEffect(() => {
    if (isConfettiEnabled && !confettiTimeout.current) {
      confettiTimeout.current = setTimeout(() => dispatch(disableConfetti()), 4000);
    } else {
      confettiTimeout.current = null;
    }
  }, [isConfettiEnabled, dispatch]);

  return (
    <main className="flex h-full min-h-0 flex-col overflow-hidden transition-all duration-300">
      {isConfettiEnabled && <ConfettiOverlay />}
      {children}
    </main>
  );
};

export default Layout;
